// src/hooks/useStoneReportSync.ts
// Report write-back for approved renal stones. Folds the approved-stone set
// into ONE stable chip in the KIDNEYS/URETERS subsection, keyed by
// STONE_CHIP_ID, so re-running (approve another / deny one / edit side) updates
// that same chip in place instead of appending duplicates.
//
// First insertion goes through the FieldNode routing command. Existing chips
// are updated in one ProseMirror transaction so text and jump-target metadata
// cannot drift apart when the largest approved stone changes.
// The chip's text is composed solely by composeStoneFinding (single source of
// truth for the finding phrase).

import { useEffect } from "react";
import type { Editor } from "@tiptap/core";
import { composeStoneFinding } from "../stones/composeStoneFinding";
import type { StoneFinding, StoneVerdict, StoneLocation, StoneLaterality } from "../stones/types";
import { effectiveStone } from "../stones/burden";
import { findFieldNodeById } from "../components/tiptap/extensions";

export const STONE_CHIP_ID = "stones-kidneys-ureters";

export function syncStoneReportChip(editor: Editor, approved: StoneFinding[]): void {
  const { text, target } = composeStoneFinding(approved);
  const hit = findFieldNodeById(editor.state.doc, STONE_CHIP_ID);
  if (hit) {
    if (text === "") {
      // A zero-length atomic chip renders as a blank KIDNEYS/URETERS row and
      // still owns the stale jump target. Remove it rather than presenting an
      // empty finding as report content.
      editor.view.dispatch(editor.state.tr.delete(hit.pos, hit.pos + hit.node.nodeSize));
      return;
    }
    editor.view.dispatch(
      editor.state.tr.setNodeMarkup(hit.pos, undefined, {
        ...hit.node.attrs,
        text,
        seriesId: target?.seriesId ?? null,
        sliceIndex: target?.sliceIndex ?? null,
        frameOfReferenceUid: target?.frameOfReferenceUid ?? null,
      }),
    );
    return;
  }
  if (text === "") return;
  const ok = editor.commands.acceptIntoSection({
    section: "kidneys-ureters",
    fieldAttrs: {
      id: STONE_CHIP_ID,
      kind: "ai-finding",
      confidence: "confident",
      condition: "kidneys-ureters/calculus",
      text,
      seriesId: target?.seriesId ?? null,
      sliceIndex: target?.sliceIndex ?? null,
      frameOfReferenceUid: target?.frameOfReferenceUid ?? null,
    },
  });
  if (!ok)
    console.error(
      "[stone-report-sync] injection failed — kidneys-ureters section missing?",
      STONE_CHIP_ID,
    );
}

export function useStoneReportSync(
  editorRef: { current: Editor | null },
  stones: StoneFinding[],
  verdicts: Record<string, StoneVerdict>,
  edits: Record<string, { location?: StoneLocation; laterality?: StoneLaterality }>,
  // Bumped by the parent whenever report content is (re)applied via setContent,
  // which wipes the injected chip. A change here re-runs the sync so the chip is
  // re-inserted (syncStoneReportChip is idempotent — finds no chip → inserts).
  rev: number = 0,
): void {
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const approved = stones
      .filter((s) => verdicts[s.id] === "approved")
      .map((s) => effectiveStone(s, edits[s.id]));
    syncStoneReportChip(editor, approved);
  }, [editorRef, stones, verdicts, edits, rev]);
}
